"use client"
import React, { useMemo } from "react";
import { useAppData } from "@/lib/providers/AppDataProvider";
import useUtils from "@/components/shared/useUtils";

import "@/components/locator/LocatorCategoryPanel.css";

// the only hard-coded part of the category panel: which macro types (types.json) sit under which heading.
// Labels, colors and icons come from the server's categories; macro types not listed here land in "Other".
const CATEGORY_GROUPINGS = [
  { name: "Food & Drink", keys: ["food", "grocery", "liquor"] },
  { name: "Retail & Services", keys: ["retail", "services", "auto"] },
  { name: "Entertainment", keys: ["entertainment"] },
  { name: "Health & Medical", keys: ["health", "medical"] },
  { name: "Travel & Lodging", keys: ["travel", "lodging"] },
  { name: "Civic & Education", keys: ["government", "education"] },
  { name: "Community", keys: ["nonprofit", "worship"] },
  { name: "Professional", keys: ["financial", "office", "developer", "manufacturing"] }
];

// Business categories shown beneath the locator search bar while it has focus.
const LocatorCategoryPanel = ({
  // macroType -> { color, icon } from the community's map configuration
  pinStyles,
  // (macroType option from the server's business types, or an extra category's option) => void
  onCategorySelected,
  // groups shown after the macro type groups, as { name, categories: [{ key, label, color, icon, option }] };
  // e.g. the real estate layer's "Development" categories, which aren't macro types
  extraGroups
}) => {
  const { flattenedBusinessTypes } = useAppData();
  const utils = useUtils();

  const categoryGroups = useMemo(() => {
    const macroTypes = (flattenedBusinessTypes ?? []).filter(
      type => type.group == null && type.value !== "special"
    );
    const byKey = Object.fromEntries(macroTypes.map(type => [type.value, type]));
    // community pin styles override the macro type defaults from types.json
    const toCategory = type => ({
      option: type,
      key: type.value,
      label: type.label,
      color: pinStyles?.[type.value]?.color ?? type.color,
      icon: pinStyles?.[type.value]?.icon ?? type.icon
    });
    const groupedKeys = new Set(CATEGORY_GROUPINGS.flatMap(grouping => grouping.keys));
    const groups = CATEGORY_GROUPINGS.map(grouping => ({
      name: grouping.name,
      categories: grouping.keys.filter(key => byKey[key]).map(key => toCategory(byKey[key]))
    }));
    groups.push({
      name: "Other",
      categories: macroTypes.filter(type => !groupedKeys.has(type.value)).map(toCategory)
    });
    return groups
      .concat(extraGroups ?? [])
      .filter(group => group.categories.length > 0)
      .map(group => ({ ...group, color: group.categories[0].color }));
  }, [flattenedBusinessTypes, pinStyles, extraGroups]);

  if (categoryGroups.length === 0) return null;

  return (
    // keep focus on the search input so the panel isn't closed (by the input's blur) before a click lands
    <div className="locator-category-panel" onMouseDown={evt => evt.preventDefault()}>
      {categoryGroups.map(group => (
        <div key={group.name} className="category-group-card">
          <div className="category-group-header" style={{ borderBottomColor: group.color }}>
            {group.name}
          </div>
          {group.categories.map(cat => (
            <button
              type="button"
              key={cat.key}
              className="category-group-item"
              onClick={() => onCategorySelected(cat.option)}
            >
              {/* the same white-icon-in-a-colored-circle marker the map draws for this category */}
              <img
                className="category-marker"
                src={utils.markerUrl(cat.color?.replace("#", ""), cat.icon, "CIRCLE")}
                alt=""
              />
              {cat.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default LocatorCategoryPanel;
